/**
 * WeaponSystem - Manages soldier auto-shooting
 *
 * Features:
 * - Each soldier fires independently
 * - Fires straight upward from each soldier position
 * - 4 weapon types with different fire rates
 * - Weapon upgrades during level
 * - Object pooling for bullets (performance)
 * - Spatial grid for collision detection (performance)
 */

import Phaser from 'phaser';
import { EFFECTS, WeaponType, WEAPONS, WEAPON_SEQUENCE } from '../utils/constants';
import { COLORS } from '../config';
import { Player } from '../entities/Player';
import { Soldier } from '../entities/Soldier';
import { Bullet } from '../entities/Bullet';
import { Enemy } from '../entities/Enemy';
import { WeaponBox } from '../entities/WeaponBox';
import { BulletPool } from './BulletPool';
import { SpatialGrid } from './SpatialGrid';
import { audioSystem } from './AudioSystem';
import { getParticlePool } from './ParticlePool';
import { getProjectileVisualStride } from './weaponVisualDensity';
import { getLowCountAimAssistPadding, getWeaponAfterPickup } from './weaponBalance';

export interface WeaponSystemConfig {
  player: Player;
  getEnemies: () => Enemy[];
  getWeaponBoxes: () => WeaponBox[];
  getIsPaused: () => boolean;
  rangeOverride?: number;
  fireRateMultiplier?: number;
  primeFirstShot?: boolean;
  onShotFired?: (event: {
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    weapon: WeaponType;
    timestamp: number;
  }) => void;
  onShotHit?: (event: { weapon: WeaponType }) => void;
}

export class WeaponSystem {
  private static readonly NO_TARGET_Y = -64;
  private static readonly SHOOT_SFX_COOLDOWN_MS = 70;

  private scene: Phaser.Scene;
  private player: Player;
  private getEnemies: () => Enemy[];
  private getIsPaused: () => boolean;

  /** Current weapon type for all soldiers */
  private currentWeapon: WeaponType = WeaponType.Pistol;

  /** Tracking last fire time per soldier (by index) */
  private soldierLastFired: Map<number, number> = new Map();

  /** Bullet object pool */
  private bulletPool: BulletPool;

  /** Spatial grid for collision detection */
  private spatialGrid: SpatialGrid;

  private readonly fireRateMultiplier: number;
  private readonly primeFirstShot: boolean;
  private readonly onShotFired?: WeaponSystemConfig['onShotFired'];
  private readonly onShotHit?: WeaponSystemConfig['onShotHit'];
  private lastShootSfxAt: number = -Infinity;

  constructor(scene: Phaser.Scene, config: WeaponSystemConfig) {
    this.scene = scene;
    this.player = config.player;
    this.getEnemies = config.getEnemies;
    this.getIsPaused = config.getIsPaused;
    this.fireRateMultiplier = Math.max(0.25, config.fireRateMultiplier ?? 1);
    this.primeFirstShot = config.primeFirstShot ?? false;
    this.onShotFired = config.onShotFired;
    this.onShotHit = config.onShotHit;

    // Initialize bullet pool (pre-allocates bullets)
    this.bulletPool = new BulletPool(scene, {
      initialSize: 600,
      maxSize: 900,
    });

    // Initialize spatial grid for collision detection
    this.spatialGrid = new SpatialGrid(60); // 60px cells
  }

  /**
   * Updates the weapon system each frame
   */
  update(time: number, delta: number): void {
    if (this.getIsPaused()) return;

    // Update existing bullets via pool
    this.bulletPool.update(time, delta);

    // Check for collisions
    this.checkCollisions();

    // Process soldier shooting
    this.processShooting(time);
  }

  /**
   * Checks bullet-enemy collisions
   * Uses spatial grid when bullet count is high enough to benefit
   */
  private checkCollisions(): void {
    const activeBullets = this.bulletPool.getActiveBullets();
    const enemies = this.getEnemies();

    // Decide whether to use spatial grid based on bullet count
    const useGrid = SpatialGrid.shouldUseGrid(activeBullets.length);

    if (useGrid) {
      this.checkCollisionsWithGrid(activeBullets, enemies);
    } else {
      this.checkCollisionsBruteForce(activeBullets, enemies);
    }
  }

  /**
   * Brute-force collision check (O(n²) but lower overhead for small counts)
   */
  private checkCollisionsBruteForce(bullets: Bullet[], enemies: Enemy[]): void {
    for (const bullet of bullets) {
      if (!bullet.getIsActive()) continue;

      for (const enemy of enemies) {
        if (!enemy.getIsAlive()) continue;

        if (this.checkBulletEnemyCollision(bullet, enemy, enemies)) {
          break; // Bullet hit something, move to next bullet
        }
      }
    }
  }

  /**
   * Grid-based collision check (O(n) with spatial partitioning)
   */
  private checkCollisionsWithGrid(bullets: Bullet[], enemies: Enemy[]): void {
    // Rebuild grid with current enemy positions
    this.spatialGrid.clear();
    this.spatialGrid.insertAll(enemies);

    for (const bullet of bullets) {
      if (!bullet.getIsActive()) continue;

      // Only check enemies in nearby cells
      const nearbyEnemies = this.spatialGrid.getNearby(bullet.x, bullet.y);

      for (const enemy of nearbyEnemies) {
        if (!enemy.getIsAlive()) continue;

        if (this.checkBulletEnemyCollision(bullet, enemy, enemies)) {
          break; // Bullet hit something, move to next bullet
        }
      }
    }
  }

  /**
   * Checks collision between a single bullet and enemy
   * Returns true if collision occurred
   */
  private checkBulletEnemyCollision(
    bullet: Bullet,
    enemy: Enemy,
    allEnemies: Enemy[]
  ): boolean {
    const dx = bullet.x - enemy.x;
    const dy = bullet.y - enemy.y;
    // Use squared distance to avoid sqrt (faster)
    const distSq = dx * dx + dy * dy;
    const collisionDist =
      bullet.getCollisionRadius() +
      enemy.getCollisionRadius() +
      getLowCountAimAssistPadding(this.player.getSoldierCount());
    const collisionDistSq = collisionDist * collisionDist;

    if (distSq < collisionDistSq) {
      // Hit!
      const damage = bullet.getDamage();

      if (bullet.isAOE()) {
        // AOE damage: hit multiple nearby enemies
        this.handleAOEHit(bullet, allEnemies);
      } else {
        enemy.takeDamage(damage);
      }

      this.spawnHitSpark(bullet.x, bullet.y);
      this.onShotHit?.({ weapon: bullet.getWeaponType() });

      bullet.deactivate();
      return true;
    }

    return false;
  }

  /**
   * Handles AOE weapon hit (rockets)
   */
  private handleAOEHit(bullet: Bullet, enemies: Enemy[]): void {
    const damage = bullet.getDamage();
    const maxTargets = bullet.getAOETargets();
    const aoeRadius = 50; // AOE splash radius
    const aoeRadiusSq = aoeRadius * aoeRadius;

    // Find enemies in AOE range using squared distance
    const nearbyEnemies = enemies
      .filter(e => e.getIsAlive())
      .map(e => {
        const dx = e.x - bullet.x;
        const dy = e.y - bullet.y;
        return {
          enemy: e,
          distSq: dx * dx + dy * dy,
        };
      })
      .filter(e => e.distSq < aoeRadiusSq)
      .sort((a, b) => a.distSq - b.distSq)
      .slice(0, maxTargets);

    for (const { enemy } of nearbyEnemies) {
      enemy.takeDamage(damage);
    }

    // AOE impact spark at center
    this.spawnHitSpark(bullet.x, bullet.y);
  }

  /**
   * Spawns a quick impact spark burst
   */
  private spawnHitSpark(x: number, y: number): void {
    const pool = getParticlePool();
    const {
      minParticles,
      maxParticles,
      minSize,
      maxSize,
      minVelocity,
      maxVelocity,
      minDuration,
      maxDuration,
      alpha,
    } = EFFECTS.hitSpark;

    const count = Phaser.Math.Between(minParticles, maxParticles);

    if (pool) {
      pool.spawnBurst(x, y, 'circle', COLORS.player, count, {
        minSize,
        maxSize,
        minVelocity,
        maxVelocity,
        minDuration,
        maxDuration,
        alpha,
      });
      return;
    }

    // Fallback: quick manual burst
    for (let i = 0; i < count; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(COLORS.player, alpha);
      const size = Phaser.Math.Between(minSize, maxSize);
      particle.fillCircle(0, 0, size);
      particle.setPosition(x, y);

      const angle = (i / count) * Math.PI * 2;
      const velocity = Phaser.Math.Between(minVelocity, maxVelocity);
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;

      this.scene.tweens.add({
        targets: particle,
        x: particle.x + vx * 0.2,
        y: particle.y + vy * 0.2,
        alpha: 0,
        duration: Phaser.Math.Between(minDuration, maxDuration),
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }

  /**
   * Processes shooting for all soldiers
   */
  private processShooting(time: number): void {
    const soldiers = this.player.getSoldiers();
    if (soldiers.length === 0) return;

    const fireInterval = 1000 / (WEAPONS[this.currentWeapon].fireRate * this.fireRateMultiplier);
    const phaseBucketCount = Math.max(1, Math.min(8, soldiers.length));

    for (let i = 0; i < soldiers.length; i++) {
      const soldier = soldiers[i];
      const lastFired = this.soldierLastFired.has(i)
        ? this.soldierLastFired.get(i)!
        : this.primeFirstShot
          ? time - fireInterval + ((i % phaseBucketCount) * fireInterval) / phaseBucketCount
          : 0;

      // Check if soldier can fire
      if (time - lastFired < fireInterval) continue;

      // Fire continuously in a straight line from each soldier position.
      this.fireBullet(soldier, soldier.x, WeaponSystem.NO_TARGET_Y, time, i, soldiers.length);
      this.soldierLastFired.set(i, time);
    }
  }

  /**
   * Fires a bullet from a soldier toward an enemy
   * Uses bullet pool for better performance
   */
  private fireBullet(
    soldier: Soldier,
    targetX: number,
    targetY: number,
    time: number,
    soldierIndex: number,
    soldierCount: number
  ): void {
    const visualStride = getProjectileVisualStride(soldierCount);
    const volleyIndex = Math.floor(time / Math.max(1, 1000 / WEAPONS[this.currentWeapon].fireRate));
    const renderVisible = soldierIndex % visualStride === volleyIndex % visualStride;
    const bullet = this.bulletPool.get({
      x: soldier.x,
      y: soldier.y,
      targetX,
      targetY,
      weaponType: this.currentWeapon,
      renderVisible,
    });

    // If pool is exhausted, skip this shot
    if (!bullet) return;

    // Downsample muzzle flashes at high counts to keep large swarms performant.
    if (this.shouldRenderMuzzleFlash(soldierIndex, soldierCount)) {
      soldier.playShootAnimation();
      this.createMuzzleFlash(soldier.x, soldier.y);
    }

    // Global cooldown avoids stacking hundreds of overlapping shoot sounds.
    if (time - this.lastShootSfxAt >= WeaponSystem.SHOOT_SFX_COOLDOWN_MS) {
      audioSystem.play('shoot', 0.75);
      this.lastShootSfxAt = time;
    }

    this.onShotFired?.({
      x: soldier.x,
      y: soldier.y,
      targetX,
      targetY,
      weapon: this.currentWeapon,
      timestamp: time,
    });
  }

  private shouldRenderMuzzleFlash(soldierIndex: number, soldierCount: number): boolean {
    if (soldierCount <= 12) return true;
    if (soldierCount <= 36) return soldierIndex % 3 === 0;
    if (soldierCount <= 72) return soldierIndex % 6 === 0;
    if (soldierCount <= 120) return soldierIndex % 10 === 0;
    if (soldierCount <= 180) return soldierIndex % 14 === 0;
    return soldierIndex % 18 === 0;
  }

  /**
   * Creates a muzzle flash effect at the given position
   * Brief visual flash that fades quickly
   */
  private createMuzzleFlash(x: number, y: number): void {
    const flash = this.scene.add.graphics();

    // Main flash circle (bright cyan)
    flash.fillStyle(0x00E5FF, 0.8);
    flash.fillCircle(0, 0, 6);

    // Inner bright core
    flash.fillStyle(0xFFFFFF, 0.9);
    flash.fillCircle(0, 0, 3);

    flash.setPosition(x, y - 5); // Slightly above soldier
    flash.setDepth(25); // Above soldiers, below UI

    // Outer ring for extra punch
    const ring = this.scene.add.graphics();
    ring.lineStyle(2, 0x00E5FF, 0.6);
    ring.strokeCircle(0, 0, 8);
    ring.setPosition(x, y - 5);
    ring.setDepth(24);

    // Quick fade out
    this.scene.tweens.add({
      targets: [flash, ring],
      alpha: 0,
      scaleX: 1.5,
      scaleY: 1.5,
      duration: 50,
      ease: 'Power2',
      onComplete: () => {
        flash.destroy();
        ring.destroy();
      },
    });
  }

  /**
   * Upgrades to the next weapon in the sequence
   */
  upgradeWeapon(): WeaponType {
    const currentIndex = WEAPON_SEQUENCE.indexOf(this.currentWeapon);
    const nextIndex = Math.min(currentIndex + 1, WEAPON_SEQUENCE.length - 1);
    this.currentWeapon = WEAPON_SEQUENCE[nextIndex];

    return this.currentWeapon;
  }

  /** Equips the weapon explicitly printed on a destroyed upgrade crate. */
  equipWeaponPickup(offeredWeapon: WeaponType): WeaponType {
    this.currentWeapon = getWeaponAfterPickup(this.currentWeapon, offeredWeapon);
    return this.currentWeapon;
  }

  /**
   * Resets weapon to pistol (start of level)
   */
  resetWeapon(): void {
    this.currentWeapon = WeaponType.Pistol;
    this.soldierLastFired.clear();
    this.bulletPool.releaseAll();
  }

  /**
   * Gets current weapon type
   */
  getCurrentWeapon(): WeaponType {
    return this.currentWeapon;
  }

  /**
   * Gets all active bullets (for external collision checks if needed)
   */
  getBullets(): Bullet[] {
    return this.bulletPool.getActiveBullets();
  }

  /**
   * Gets bullet pool statistics (for performance monitoring)
   */
  getBulletPoolStats(): ReturnType<BulletPool['getStats']> {
    return this.bulletPool.getStats();
  }

  /**
   * Gets spatial grid statistics (for performance monitoring)
   */
  getGridStats(): ReturnType<SpatialGrid['getStats']> {
    return this.spatialGrid.getStats();
  }

  /**
   * Updates player reference
   */
  setPlayer(player: Player): void {
    this.player = player;
    this.soldierLastFired.clear();
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.bulletPool.destroy();
    this.soldierLastFired.clear();
  }
}
